import type { RxJsonSchema } from 'rxdb'

/**
 * PersonalNote Document Type
 * Represents a private daily or weekly journal note for a specific user.
 */
export interface PersonalNoteDocument {
  id: string
  envId: string
  userId: string
  noteType: string // 'daily' | 'weekly'
  noteDate: string // "2026-03-29" for daily, "2026-W13" for weekly
  title: string
  isDeleted: boolean

  // Audit fields
  createdOn: number
  createdBy: string
  updatedOn: number
  updatedBy: string
  deletedOn?: number | null
  deletedBy?: string | null

  // RxDB replication metadata
  updatedAt?: number
}

/**
 * RxDB Schema for PersonalNote
 */
export const personalNoteSchema: RxJsonSchema<PersonalNoteDocument> = {
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: {
      type: 'string',
      maxLength: 36,
    },
    envId: {
      type: 'string',
      maxLength: 36,
    },
    userId: {
      type: 'string',
      maxLength: 36,
    },
    noteType: {
      type: 'string',
      enum: ['daily', 'weekly'],
      maxLength: 10,
    },
    noteDate: {
      type: 'string',
      maxLength: 10,
    },
    title: {
      type: 'string',
      maxLength: 500,
    },
    isDeleted: {
      type: 'boolean',
      default: false,
    },

    // Audit fields
    createdOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    createdBy: {
      type: 'string',
      maxLength: 36,
    },
    updatedOn: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    updatedBy: {
      type: 'string',
      maxLength: 36,
    },
    deletedOn: {
      type: ['number', 'null'],
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
    deletedBy: {
      type: ['string', 'null'],
      maxLength: 36,
    },

    // RxDB replication metadata
    updatedAt: {
      type: 'number',
      minimum: 0,
      maximum: 9999999999999,
      multipleOf: 1,
    },
  },
  required: [
    'id',
    'envId',
    'userId',
    'noteType',
    'noteDate',
    'title',
    'isDeleted',
    'createdOn',
    'createdBy',
    'updatedOn',
    'updatedBy',
  ],
  indexes: [
    'envId',
    'updatedOn',
    ['envId', 'isDeleted'],
    ['envId', 'userId', 'noteType'],
  ],
}
